import React from "react";
import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Box,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FolderType } from "../../types/interfaces/filesStorage";

interface FolderTypeOption {
  value: FolderType;
  label: string;
}

const BASE_FOLDER_TYPE_OPTIONS: FolderTypeOption[] = [
  { value: "all", label: "All Folders" },
  { value: "integration", label: "Integration Folders" },
  { value: "migration", label: "Migration Folders" },
];

const SFTP_OPTION: FolderTypeOption = {
  value: "sftp",
  label: "SFTP Folders",
};

interface FolderTypeFilterProps {
  value: FolderType;
  onChange: (value: FolderType) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  showSftpFilter?: boolean;
}

const FolderTypeFilter: React.FC<FolderTypeFilterProps> = ({
  value,
  onChange,
  disabled = false,
  disabledTooltip = "Filter only available at root level",
  showSftpFilter = false,
}) => {
  const handleChange = (event: SelectChangeEvent<FolderType>) => {
    onChange(event.target.value as FolderType);
  };

  const folderTypeOptions =
    showSftpFilter || value === "sftp"
      ? [...BASE_FOLDER_TYPE_OPTIONS, SFTP_OPTION]
      : BASE_FOLDER_TYPE_OPTIONS;

  const selectComponent = (
    <FormControl variant="outlined" size="small" disabled={disabled}>
      <Select
        value={value}
        onChange={handleChange}
        sx={(theme) => ({
          minWidth: 180,
          height: "40px",
          color: disabled ? "text.disabled" : "primary.main",
          ".MuiSelect-icon": {
            color: disabled ? "text.disabled" : "primary.main",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled
              ? theme.palette.divider
              : alpha(theme.palette.common.black, 0.23),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled ? theme.palette.divider : "primary.main",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
          },
        })}
      >
        {folderTypeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (disabled) {
    return (
      <Tooltip title={disabledTooltip} arrow placement="top">
        <Box component="span" sx={{ display: "inline-flex" }}>
          {selectComponent}
        </Box>
      </Tooltip>
    );
  }

  return selectComponent;
};

export default FolderTypeFilter;
